import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type Cohort = Database['public']['Tables']['cohorts']['Row'];
type CohortInsert = Database['public']['Tables']['cohorts']['Insert'];
type CohortUpdate = Database['public']['Tables']['cohorts']['Update'];
type CohortMembership = Database['public']['Tables']['cohort_memberships']['Row'];

export function useCohorts() {
  return useQuery({
    queryKey: ['cohorts'],
    queryFn: async (): Promise<Cohort[]> => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - cohorts rarely change
  });
}

export function useAllCohorts() {
  return useQuery({
    queryKey: ['cohorts', 'all'],
    queryFn: async (): Promise<Cohort[]> => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export interface CohortWithStats extends Cohort {
  memberCount: number;
  postCount: number;
}

export function useAllCohortsWithStats() {
  return useQuery({
    queryKey: ['cohorts', 'all-with-stats'],
    queryFn: async (): Promise<CohortWithStats[]> => {
      const { data: cohorts, error: cohortsError } = await supabase
        .from('cohorts')
        .select('*')
        .order('name');

      if (cohortsError) throw cohortsError;
      if (!cohorts || cohorts.length === 0) return [];

      // Fetch member counts
      const { data: memberships, error: membError } = await supabase
        .from('cohort_memberships')
        .select('cohort_id');

      if (membError) throw membError;

      const memberCounts = new Map<string, number>();
      for (const m of memberships || []) {
        memberCounts.set(m.cohort_id, (memberCounts.get(m.cohort_id) || 0) + 1);
      }

      // Fetch post counts by cohort_tag (matches cohort city/name)
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('cohort_tag')
        .eq('is_deleted', false)
        .neq('cohort_tag', '');

      if (postsError) throw postsError;

      const postCounts = new Map<string, number>();
      for (const p of posts || []) {
        if (p.cohort_tag) {
          const key = p.cohort_tag.trim().toLowerCase();
          postCounts.set(key, (postCounts.get(key) || 0) + 1);
        }
      }

      return cohorts.map((cohort) => ({
        ...cohort,
        memberCount: memberCounts.get(cohort.id) || 0,
        postCount:
          postCounts.get((cohort.city || '').trim().toLowerCase()) ||
          postCounts.get(cohort.name.trim().toLowerCase()) ||
          0,
      }));
    },
  });
}

export function useCohortBySlug(slug: string) {
  return useQuery({
    queryKey: ['cohort', slug],
    queryFn: async (): Promise<Cohort | null> => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useUserCohorts() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-cohorts', user?.id],
    queryFn: async (): Promise<(CohortMembership & { cohort: Cohort })[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('cohort_memberships')
        .select('*, cohort:cohorts(*)')
        .eq('user_id', user.id);

      if (error) throw error;
      return (data || []).map((m) => ({
        ...m,
        cohort: m.cohort as Cohort,
      }));
    },
    enabled: !!user?.id,
  });
}

export function useCreateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cohort: CohortInsert) => {
      const { data, error } = await supabase
        .from('cohorts')
        .insert(cohort)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
    },
  });
}

export function useUpdateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: CohortUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('cohorts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
    },
  });
}

export function useJoinCohort() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      cohortId,
      isPrimary = false,
    }: {
      cohortId: string;
      isPrimary?: boolean;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('cohort_memberships')
        .insert({
          user_id: user.id,
          cohort_id: cohortId,
          is_primary: isPrimary,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cohorts'] });
    },
  });
}

export function useRefreshCohorts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{
      cohortsCreated: number;
      membersAssigned: number;
      channelsCreated: number;
      emptyCohorts: { id: string; name: string }[];
    }> => {
      // 1. Fetch all profiles with a city_cohort
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, city_cohort')
        .neq('city_cohort', '');

      if (profilesError) throw profilesError;
      if (!profiles || profiles.length === 0) {
        return { cohortsCreated: 0, membersAssigned: 0, channelsCreated: 0, emptyCohorts: [] };
      }

      // 2. Group profiles by city_cohort (case-insensitive)
      const cityMap = new Map<string, string[]>();
      for (const p of profiles) {
        if (!p.city_cohort) continue;
        const key = p.city_cohort.trim().toLowerCase();
        if (!key) continue;
        const existing = cityMap.get(key) || [];
        existing.push(p.user_id);
        cityMap.set(key, existing);
      }

      // 3. Fetch all existing cohorts
      const { data: existingCohorts, error: cohortsError } = await supabase
        .from('cohorts')
        .select('*');

      if (cohortsError) throw cohortsError;

      const cohortByCity = new Map<string, string>();
      for (const c of existingCohorts || []) {
        if (c.city) cohortByCity.set(c.city.trim().toLowerCase(), c.id);
        // Also index by name so we match cohorts that have a name but no city set
        if (c.name && !cohortByCity.has(c.name.trim().toLowerCase())) {
          cohortByCity.set(c.name.trim().toLowerCase(), c.id);
        }
      }

      // 4. Create missing cohorts
      let cohortsCreated = 0;
      for (const [cityKey] of cityMap) {
        if (!cohortByCity.has(cityKey)) {
          // Use the original casing from the first profile that has this city
          const originalCity = profiles.find(
            (p) => p.city_cohort?.trim().toLowerCase() === cityKey
          )?.city_cohort?.trim() || cityKey;

          const slug = originalCity
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

          const { data: newCohort, error: insertError } = await supabase
            .from('cohorts')
            .insert({ name: originalCity, slug, city: originalCity, is_active: true })
            .select()
            .single();

          if (insertError) throw insertError;
          cohortByCity.set(cityKey, newCohort.id);
          cohortsCreated++;
        }
      }

      // 5. Fetch all existing memberships
      const { data: existingMemberships, error: membershipsError } = await supabase
        .from('cohort_memberships')
        .select('user_id, cohort_id');

      if (membershipsError) throw membershipsError;

      const membershipSet = new Set(
        (existingMemberships || []).map((m) => `${m.user_id}:${m.cohort_id}`)
      );

      // 6. Insert missing memberships
      const toInsert: { user_id: string; cohort_id: string }[] = [];
      for (const [cityKey, userIds] of cityMap) {
        const cohortId = cohortByCity.get(cityKey);
        if (!cohortId) continue;
        for (const userId of userIds) {
          if (!membershipSet.has(`${userId}:${cohortId}`)) {
            toInsert.push({ user_id: userId, cohort_id: cohortId });
          }
        }
      }

      if (toInsert.length > 0) {
        const { error: insertMembError } = await supabase
          .from('cohort_memberships')
          .insert(toInsert);

        if (insertMembError) throw insertMembError;
      }

      // 7. Find the "Cohorts" section for sidebar navigation
      //    Match any section whose name contains "cohort" (case-insensitive)
      let cohortsSectionId: string | null = null;
      const { data: allSections } = await supabase
        .from('sections')
        .select('id, name')
        .eq('is_active', true);

      if (allSections) {
        const match = allSections.find((s) =>
          s.name.toLowerCase().includes('cohort')
        );
        if (match) cohortsSectionId = match.id;
      }

      // 8. Create channels for each cohort that doesn't have one yet
      let channelsCreated = 0;
      if (cohortsSectionId) {
        const { data: existingChannels } = await supabase
          .from('channels')
          .select('id, cohort_id, slug');

        const channelCohortIds = new Set(
          (existingChannels || []).filter((ch) => ch.cohort_id).map((ch) => ch.cohort_id)
        );
        const existingSlugs = new Set(
          (existingChannels || []).map((ch) => ch.slug)
        );

        // Re-fetch cohorts to include any newly created ones
        const { data: allCohorts } = await supabase
          .from('cohorts')
          .select('*')
          .eq('is_active', true);

        for (const cohort of allCohorts || []) {
          if (!channelCohortIds.has(cohort.id)) {
            // Ensure unique slug by appending "-cohort" if slug already exists
            let channelSlug = cohort.slug;
            if (existingSlugs.has(channelSlug)) {
              channelSlug = `${cohort.slug}-cohort`;
            }

            const { error: chError } = await supabase
              .from('channels')
              .insert({
                name: cohort.name,
                slug: channelSlug,
                cohort_id: cohort.id,
                section_id: cohortsSectionId,
                is_active: true,
                channel_type: 'discussion',
              });

            if (chError) throw chError;
            existingSlugs.add(channelSlug);
            channelsCreated++;
          }
        }
      }

      // 9. Identify cohorts with zero members (candidates for deletion)
      const { data: finalMemberships } = await supabase
        .from('cohort_memberships')
        .select('cohort_id');

      const finalMemberCounts = new Map<string, number>();
      for (const m of finalMemberships || []) {
        finalMemberCounts.set(m.cohort_id, (finalMemberCounts.get(m.cohort_id) || 0) + 1);
      }

      const { data: finalCohorts } = await supabase
        .from('cohorts')
        .select('id, name');

      const emptyCohorts = (finalCohorts || []).filter(
        (c) => !finalMemberCounts.has(c.id)
      );

      return { cohortsCreated, membersAssigned: toInsert.length, channelsCreated, emptyCohorts };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['user-cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });
}

export function useDeleteCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cohortId: string) => {
      const { error } = await supabase
        .from('cohorts')
        .delete()
        .eq('id', cohortId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['user-cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });
}

// Admin: fetch all cohort memberships keyed by user_id
export function useAllCohortMemberships() {
  return useQuery({
    queryKey: ['cohort-memberships', 'all'],
    queryFn: async (): Promise<Map<string, { cohort_id: string; cohort_name: string }[]>> => {
      const { data: memberships, error } = await supabase
        .from('cohort_memberships')
        .select('user_id, cohort_id, cohorts(name)');

      if (error) throw error;

      const map = new Map<string, { cohort_id: string; cohort_name: string }[]>();
      for (const m of memberships || []) {
        const entry = {
          cohort_id: m.cohort_id,
          cohort_name: (m.cohorts as any)?.name || '',
        };
        const existing = map.get(m.user_id) || [];
        existing.push(entry);
        map.set(m.user_id, existing);
      }
      return map;
    },
    staleTime: 1000 * 60,
  });
}

// Admin: set a user's cohort memberships (replace all with new set)
export function useSetUserCohorts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, cohortIds }: { userId: string; cohortIds: string[] }) => {
      // Delete all existing memberships for this user
      const { error: deleteError } = await supabase
        .from('cohort_memberships')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Insert new memberships
      if (cohortIds.length > 0) {
        const { error: insertError } = await supabase
          .from('cohort_memberships')
          .insert(
            cohortIds.map((cohortId, i) => ({
              user_id: userId,
              cohort_id: cohortId,
              is_primary: i === 0,
            }))
          );

        if (insertError) throw insertError;
      }

      // Also update profile's city_cohort to match the primary cohort
      // Fetch the name of the first cohort
      if (cohortIds.length > 0) {
        const { data: cohort } = await supabase
          .from('cohorts')
          .select('name')
          .eq('id', cohortIds[0])
          .single();

        if (cohort) {
          await supabase
            .from('profiles')
            .update({ city_cohort: cohort.name })
            .eq('user_id', userId);
        }
      } else {
        await supabase
          .from('profiles')
          .update({ city_cohort: null })
          .eq('user_id', userId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohort-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['user-cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useLeaveCohort() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (cohortId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('cohort_memberships')
        .delete()
        .eq('user_id', user.id)
        .eq('cohort_id', cohortId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cohorts'] });
    },
  });
}
